import {useQuery} from '@tanstack/react-query';
import {useConfig} from '@lore/config';

/**
 * @typedef {Object} SiteContactItem
 * @property {number} id
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} title
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} SiteContactsResponse
 * @property {SiteContactItem[]} data
 * @property {number} page
 * @property {number} pageSize
 * @property {number} total
 */

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {SiteContactItem}
 */
export const _SiteContactItem = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {SiteContactsResponse}
 */
export const _SiteContactsResponse = {};

const STALE_TIME = 15 * 60 * 1000; // 15 minutes

/**
 * Custom hook to fetch site contacts from the core API.
 *
 * @param {number|string} siteId - The ID of the site to fetch contacts for.
 * @param {Object} [params={}] - Query parameters for the request.
 * @param {number} [params.page=1] - The page number to fetch.
 * @param {number} [params.pageSize=20] - The number of items per page.
 * @param {Object} [options={}] - Configuration options like 'enabled' to control the query.
 * @returns {import('@tanstack/react-query').UseQueryResult<SiteContactsResponse>} React Query result object.
 */
export default function useCoreSiteContacts(
  siteId,
  {page = 1, pageSize = 20} = {},
  {enabled = true} = {}
) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useQuery({
    queryKey: ['sites', siteId, 'contacts', page, pageSize],

    queryFn: () => {
      const url = new URL(`${apiRoot}/core/sites/${siteId}/contacts`);

      // Add query parameters
      if (page != null) url.searchParams.append('page', String(page));
      if (pageSize != null) url.searchParams.append('pageSize', String(pageSize));

      return fetch(url.toString(), {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch contacts for site ${siteId}`);
        }
        return res.json();
      });
    },

    staleTime: STALE_TIME,
    enabled: siteId != null && !isNaN(siteId) && enabled,
  });

  return {
    ...query,
  };
}
